import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Filter as FilterIcon } from 'lucide-react';
import SavedFilterTabs from '../../../shared/SavedFilterTabs';
import FilterDrawer from '../../../shared/FilterDrawer';
import CancelButton from '../../../shared/CancelButton';
import DateInput from '../../../shared/DateInput';
import { Pagination, PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize } from '../../../shared/pagination';
import { formatDate } from '../../../shared/tenantClock';
import { formatMoney } from '../../../shared/numberFormat';
import { ReceiptQuickbooksTag } from '../../../shared/receiptShared';
import { VoidPill } from '../Invoices/InvoicesTable';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';

// Receivables › Receipts (issue #442): every Receipt, newest first. Each is
// recorded on its Quote or Sales Order; a row opens its own page. A
// Receipt taken on a Quote that's since been converted is listed against
// the Sales Order. A voided Receipt stays listed, greyed and marked Void.
const FILTER_SCREEN_KEY = 'receipts';
const DEFAULT_FILTERS = { status: '', customer: '', date_from: '', date_to: '' };
const FIELD_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };
const TH = { padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' };

function ReceiptsTable() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  const [search, setSearch] = useState('');
  // A dashboard tile can open this list already filtered (initialFilters).
  const location = useLocation();
  const [filters, setFilters] = useState(() => ({ ...DEFAULT_FILTERS, ...(location.state?.initialFilters || {}) }));
  const applyFilters = (values) => { setFilters(values); setPage(1); };
  const [filterDraft, setFilterDraft] = useState(null);
  const activeFilterCount = Object.keys(DEFAULT_FILTERS).filter((k) => String(filters[k] || '') !== String(DEFAULT_FILTERS[k] || '')).length;
  const openFilterPanel = () => setFilterDraft({ ...filters });
  const closeFilterPanel = () => setFilterDraft(null);
  const setFilterDraftField = (key, value) => setFilterDraft((prev) => ({ ...prev, [key]: value }));
  const applyFilterPanel = () => { applyFilters(filterDraft); closeFilterPanel(); };
  const clearFilterPanel = () => { applyFilters(DEFAULT_FILTERS); closeFilterPanel(); };

  useDefaultPageSize(setLimit);

  useEffect(() => {
    setLoading(true);
    axios.get('/api/receipts', {
      params: {
        search: search || undefined,
        status: filters.status || undefined,
        customer: filters.customer || undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        page,
        limit,
      },
    })
      .then((res) => {
        setRows(res.data.data || []);
        setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
      })
      .catch(() => {
        setRows([]);
        setPagination({ page, limit, total: 0, pages: 1 });
      })
      .finally(() => setLoading(false));
  }, [search, filters, page, limit]);

  // Shared by the Filters drawer and SavedFilterTabs' own "+ New" tab
  // editor (ui-architecture.md's Single-Row Filters).
  const renderDrawerFields = (draft, setDraftField) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <label style={FIELD_LABEL_STYLE}>Status</label><br />
        <select className="styled-input" value={draft.status || ''} onChange={(e) => setDraftField('status', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All</option>
          <option value="recorded">Recorded</option>
          <option value="void">Void</option>
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Customer</label><br />
        <input className="styled-input" value={draft.customer} onChange={(e) => setDraftField('customer', e.target.value)} style={FIELD_INPUT_STYLE} />
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Paid from</label><br />
        <DateInput className="styled-input" value={draft.date_from} onChange={(e) => setDraftField('date_from', e.target.value)} style={FIELD_INPUT_STYLE} />
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Paid to</label><br />
        <DateInput className="styled-input" value={draft.date_to} onChange={(e) => setDraftField('date_to', e.target.value)} style={FIELD_INPUT_STYLE} />
      </div>
    </div>
  );

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Receipts</h2>
      </div>

      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <input
            className="styled-input"
            placeholder="Receipt #, Customer or Quote/Order #..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ width: 270 }}
          />
          {search && (
            <button type="button" onClick={() => { setSearch(''); setPage(1); }} title="Clear search" style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12 }}>×</button>
          )}
        </div>
        <button
          type="button"
          onClick={openFilterPanel}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6, border: '1px solid #bfc8d6', background: activeFilterCount ? '#eef1f5' : '#fff', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}
        >
          <FilterIcon size={15} strokeWidth={2} />
          Filters
          {activeFilterCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: '#1976d2', color: '#fff', fontSize: 11, fontWeight: 700 }}>
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      <div style={{ margin: '10px 24px 0' }}>
        <SavedFilterTabs
          screenKey={FILTER_SCREEN_KEY}
          defaultFilters={DEFAULT_FILTERS}
          currentFilters={filters}
          onApply={applyFilters}
          renderFields={renderDrawerFields}
        />
      </div>

      {filterDraft && (
        <FilterDrawer
          open
          onOpenChange={(next) => { if (!next) closeFilterPanel(); }}
          title="Filters"
          description="Narrow the Receipts list by status, Customer or date paid."
          footer={(
            <>
              <div>
                <button type="button" onClick={clearFilterPanel} style={{ padding: '8px 12px', border: '1px solid #bfc8d6', color: '#5a6b85', background: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Clear filters
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <CancelButton onClick={closeFilterPanel} />
                <button type="button" onClick={applyFilterPanel} style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Apply
                </button>
              </div>
            </>
          )}
        >
          {renderDrawerFields(filterDraft, setFilterDraftField)}
        </FilterDrawer>
      )}

      <div style={{ margin: '18px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={TH}>Receipt #</th>
              <th style={TH}>Date Paid</th>
              <th style={TH}>Customer</th>
              <th style={TH}>Quote / Sales Order</th>
              <th style={TH}>Paid into</th>
              <th style={TH}>QuickBooks</th>
              <th style={{ ...TH, textAlign: 'right' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ padding: 14, textAlign: 'center', background: '#f6f8fa' }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 14, textAlign: 'center', background: '#f6f8fa' }}>No receipts</td></tr>
            ) : rows.map((row) => {
              const isVoid = row.status === 'void';
              return (
                <tr
                  key={row.id}
                  className="list-row"
                  onClick={() => navigate(`/receivables-receipts/${row.id}`)}
                  title="Click to open"
                  style={{ cursor: 'pointer', color: isVoid ? '#8a94a6' : undefined }}
                >
                  <td style={{ padding: '6px 8px', fontWeight: 600 }}>
                    <span style={{ textDecoration: isVoid ? 'line-through' : 'none' }}>{row.display_number}</span>
                    {isVoid && <div><VoidPill /></div>}
                  </td>
                  <td style={{ padding: '6px 8px' }}>{formatDate(row.payment_date)}</td>
                  <td style={{ padding: '6px 8px' }}>{row.customer?.name || '—'}</td>
                  <td style={{ padding: '6px 8px' }}>{(row.sales_order || row.quote)?.display_number || '—'}</td>
                  <td style={{ padding: '6px 8px', fontSize: 13 }}>{row.paid_into_label || '—'}</td>
                  <td style={{ padding: '6px 8px' }}><ReceiptQuickbooksTag receipt={row} /></td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', textDecoration: isVoid ? 'line-through' : 'none' }}>{formatMoney(row.amount)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Receipts"
        rightContent={(
          <select className="styled-input" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}>
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default ReceiptsTable;
